import { AiEntityProposalType, AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./aiFieldProposalForm";

/**
 * GraphQL AI proposals store `fieldValue` as a string; the real entity/form field is typed (number, enum code, …).
 * BoundAi* uses `pendingProposedValue` for the highlight, `resolveAiProposalStatus` when the user edits,
 * and a `parseAi*` to coerce that string into the field's type. Accept All uses the same parsers via `aiFieldBinding`.
 */

/** Coerces GraphQL's string `fieldValue` into the entity field's type `T`. */
export type AiProposalParse<T> = (raw: string | null | undefined) => T | undefined;

/** Parses pending field edits, but never treats entity DELETE intent as a replacement field value. */
export function pendingProposedValue<T>(
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined,
  parse: AiProposalParse<T>,
): T | undefined {
  if (aiProposal?.status.value !== AiFieldProposalStatus.Pending || isAiProposedDeletion(aiProposal)) return undefined;
  return parse(aiProposal.fieldValue.value);
}

/** Entity DELETE intent applies to every field, independent of the AFP's review status. */
export function isAiProposedDeletion(aiProposal: ObjectState<AiFieldProposalFormValue> | undefined): boolean {
  return aiProposal?.parentType.value === AiEntityProposalType.Delete;
}

/** Selects entity removal before field edits; a DELETE AFP can describe `active`, not a visible column. */
export function findAiFieldProposal(
  proposals: readonly ObjectState<AiFieldProposalFormValue>[],
  fieldName: string,
): ObjectState<AiFieldProposalFormValue> | undefined {
  return proposals.find(isAiProposedDeletion) ?? proposals.find((p) => p.fieldName.value === fieldName);
}

/** Marks Accepted when `nextValue` matches a valid parsed proposal (entity field type); otherwise Rejected. */
export function resolveAiProposalStatus<T>(
  aiProposal: ObjectState<AiFieldProposalFormValue> | undefined,
  parse: AiProposalParse<T>,
  nextValue: T | undefined,
): void {
  if (!aiProposal) return;
  const proposed = parse(aiProposal.fieldValue.value);
  // Only a null proposal means clear; an unknown option name must not match an empty select.
  aiProposal.status.set(
    (proposed !== undefined || aiProposal.fieldValue.value == null) && Object.is(proposed, nextValue)
      ? AiFieldProposalStatus.Accepted
      : AiFieldProposalStatus.Rejected,
  );
}

/** GraphQL `fieldValue` is always a string; number entity fields need a number. */
export function parseAiNumber(raw: string | null | undefined): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/** Pass-through when the entity field is already a string (e.g. an enum `.code`). */
export function parseAiString(raw: string | null | undefined): string | undefined {
  if (raw == null || raw === "") return undefined;
  return raw;
}

/**
 * When the agent proposed an option's `name` but the form field stores that option's `id`
 * (e.g. material brand), look the name up in the current option list.
 */
export function parseAiNamedOption(options: readonly { id: string; name: string }[]): AiProposalParse<string> {
  return (raw) => {
    if (raw == null || raw.trim() === "") return undefined;
    const formatted = raw.trim().toLocaleLowerCase();
    return options.find((o) => o.name.toLocaleLowerCase() === formatted)?.id;
  };
}
